import {
  setCertificates,
  setIsCertificateInstancesRequested,
  setIsCertificatesRequested,
  setIsCompaniesRequested,
} from '../../../Store/DataSlice'
import './CertificateDetail.scss'
import { useEffect } from 'react'
import { useLocalization } from '../../../Hooks/useLocalization'
import { useDispatch, useSelector } from 'react-redux'
import { type RootState } from '../../../Store/Store'
import {
  calculateCertificateActualStatus,
  formatCompanyBranch,
} from '../../../Utils/Formatting'
import { setDestinationView } from '../../../Store/InterfaceSlice'
import { Button } from '../../../Components/Button/Button'
import { getAuthorizationHeader } from '../../../Orchestration/Authentication'

interface CertificateDetailProps {
  uuid: string
}
export function CertificateDetail({ uuid }: CertificateDetailProps) {
  const dispatch = useDispatch()
  const localization = useLocalization('certificate')
  const countryLocalization = useLocalization('country')
  const certificateStatusesLocalization = useLocalization(
    'certificate-statuses'
  )

  const isCompaniesRequested = useSelector(
    (state: RootState) => state.data.isCompaniesRequested
  )
  useEffect(() => {
    if (!isCompaniesRequested) dispatch(setIsCompaniesRequested(true))
  }, [isCompaniesRequested, dispatch])

  const allCompanies = useSelector((state: RootState) => state.data.companies)

  const isCertificateInstancesRequested = useSelector(
    (state: RootState) => state.data.isCertificateInstancesRequested
  )
  useEffect(() => {
    if (!isCertificateInstancesRequested)
      dispatch(setIsCertificateInstancesRequested(true))
  }, [isCertificateInstancesRequested, dispatch])

  const allCertificateInstances = useSelector(
    (state: RootState) => state.data.certificateInstances
  )

  const isCertificatesRequested = useSelector(
    (state: RootState) => state.data.isCertificatesRequested
  )
  useEffect(() => {
    if (!isCertificatesRequested) dispatch(setIsCertificatesRequested(true))
  }, [isCertificatesRequested, dispatch])

  const allCertificates = useSelector(
    (state: RootState) => state.data.certificates
  )
  const certificate = allCertificates.find((c) => c.uuid === uuid)

  const user = useSelector((state: RootState) => state.authentication.user)
  const isLoggedIn = !!user
  const isAdmin = user?.isAdmin ?? false

  const status = calculateCertificateActualStatus(certificate)

  const removeCertificate = () => {
    if (certificate == null) return

    if (
      !confirm(
        localization.text(
          'confirm-certificate-delete',
          'Bent u zeker dat u dit certificaat wilt verwijderen?'
        )
      )
    )
      return

    fetch(
      `https://qrzszdu3vd.execute-api.eu-west-1.amazonaws.com/default/certificate-delete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthorizationHeader(),
        },
        body: JSON.stringify({
          certificateUuid: certificate.uuid,
        }),
      }
    )
      .then(() => {
        dispatch(
          setCertificates(
            allCertificates.filter((c) => c.uuid !== certificate.uuid)
          )
        )
        setTimeout(() => {
          dispatch(setDestinationView({ destinationView: 'certificate-list' }))
        }, 1)
      })
      .catch((err) => {
        console.warn(`fetch certificate-delete failed: `, err)
      })
  }

  return (
    <div className="certificate-detail">
      {!certificate ? (
        <div className="loading">
          {localization.text(
            'company-is-loading',
            'De onderneming word momenteel ingeladen.'
          )}
        </div>
      ) : (
        <>
          {isLoggedIn ? (
            <div className="edit-button-wrapper">
              <Button
                size="regular"
                importance="primary"
                onClick={() =>
                  dispatch(
                    setDestinationView({
                      destinationView: 'certificate-edit',
                      detailKey: certificate.uuid,
                    })
                  )
                }
                text={localization.text('edit', 'Editeer certificaat')}
              />
              {isAdmin ? (
                <Button
                  size="regular"
                  importance="primary"
                  onClick={removeCertificate}
                  text={localization.text('delete', 'Verwijder certificaat')}
                />
              ) : null}
            </div>
          ) : null}

          <div className="display-field">
            <div className="display-label">
              {localization.text('certificateNumber', 'Certificaat nummer')}
            </div>
            <div className="display-value">{certificate.certificateNumber}</div>
          </div>

          <div className="display-field">
            <div className="display-label">
              {localization.text('naceCodes', 'NACE codes')}
            </div>
            <div className="display-value">
              {certificate.naceCodes?.join(', ')}
            </div>
          </div>

          <div className="display-field">
            <div className="display-label">
              {localization.text('scope', 'Scope')}
            </div>
            <div className="display-value">{certificate.scope}</div>
          </div>

          <div className="company-branches">
            <div className="label">
              {localization.text(
                'companyBranches',
                'Ondernemingen en vestigingen'
              )}
            </div>
            {certificate.companyBranches?.map((cb) => {
              const company = allCompanies.find(
                (c) => c.vatNumber === cb.vatNumber
              )
              if (!company) return null
              return (
                <div className="company-option" key={`${company.vatNumber}`}>
                  <div className="company-name">
                    <span
                      className="navigate-action"
                      onClick={() => {
                        dispatch(
                          setDestinationView({
                            destinationView: 'company-detail',
                            detailKey: company.vatNumber,
                          })
                        )
                      }}
                    >
                      {company.name}
                    </span>
                  </div>
                  {certificate.isGroupCertificate && (
                    <div className="company-branches">
                      {company.branches
                        .filter((b) => cb.branchNumbers?.includes(b.number))
                        .map((b) => (
                          <div
                            className="company-branch"
                            key={`${company.vatNumber}-${b.number}`}
                          >
                            {formatCompanyBranch(b, countryLocalization)}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="display-field">
            <div className="display-label">
              {localization.text('status', 'Status')}
            </div>
            <div className="display-value">
              {certificateStatusesLocalization.text(status, status)}
            </div>
          </div>

          <div className="display-field">
            <div className="display-label">
              {localization.text(
                'certificateInstances',
                'Certificatie instantie'
              )}
            </div>
            <div className="display-value">
              <span
                className="navigate-action"
                onClick={() => {
                  dispatch(
                    setDestinationView({
                      destinationView: 'certificate-instance-detail',
                      detailKey: certificate.certificateInstanceVatNumber,
                    })
                  )
                }}
              >
                {allCertificateInstances.find(
                  (c) =>
                    c.vatNumber === certificate.certificateInstanceVatNumber
                )?.name ?? ''}
              </span>
            </div>
          </div>

          <div className="display-field">
            <div className="display-label">
              {localization.text('type', 'Type')}
            </div>
            <div className="display-value">{certificate.type}</div>
          </div>

          <div className="display-field">
            <div className="display-label">
              {localization.text('issueDate', 'Uitgereikt op')}
            </div>
            <div className="display-value">
              {new Date(certificate.issueDate).toLocaleDateString()}
            </div>
          </div>

          <div className="display-field">
            <div className="display-label">
              {localization.text('validFrom', 'Geldig van')}
            </div>
            <div className="display-value">
              {certificate.validFrom != null
                ? new Date(certificate.validFrom).toLocaleDateString()
                : ''}
            </div>
          </div>

          <div className="display-field">
            <div className="display-label">
              {localization.text('validUntil', 'Geldig tot')}
            </div>
            <div className="display-value">
              {certificate.validUntil != null
                ? new Date(certificate.validUntil).toLocaleDateString()
                : ''}
            </div>
          </div>

          {certificate.extendedUntil != null ? (
            <div className="display-field">
              <div className="display-label">
                {localization.text('extendedUntil', 'Verlengt tot')}
              </div>
              <div className="display-value">
                {certificate.extendedUntil != null
                  ? new Date(certificate.extendedUntil).toLocaleDateString()
                  : ''}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
